import React, { useState, useEffect } from 'react';
import { formatDate, formatDistanceToNow, set } from 'date-fns';
import { DataBox } from './FieldComponents';
import { EditDetail } from './EditDetail';
import '../styles/Intake.css';
import { StarOutlineSharp, StarSharp } from '@mui/icons-material';
import { Check, Clock, RefreshCw } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useNavVariant } from '../App';
import { formatIntakeMessageReceivedAt } from '../utils/intakeMessages';

export const Intake = () => {
    const [leads, setLeads] = useState([]);
    const [opportunities, setOpportunities] = useState([]);
    const [messages, setMessages] = useState([]);
    const navVariant = useNavVariant();
    const [recentlyUpdated, setRecentlyUpdated] = useState([]);
    const [editContact, setEditContact] = useState(false);
    const [contactData, setContactData] = useState(null);
    const [error, setError] = useState(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [showRefreshCheck, setShowRefreshCheck] = useState(false);
    const [leadCounts, setLeadCounts] = useState({
        today: 0,
        this_month: 0,
        last_month: 0,
        wanted_today: 0,
        wanted_this_month: 0,
        wanted_last_month: 0,
        signups_today: 0,
        signups_this_month: 0,
        signups_last_month: 0
    });
    const caseLikelihood = ["No Case", "Unlikely Case", "Possible Case", "Likely Case", "Very Likely Case"];
    const navigate = useNavigate();

    // Helper function to properly parse UTC dates
    const parseUTCDate = (dateString) => {
        if (!dateString) return null;
        // Ensure the date string is treated as UTC by adding 'Z' if not present
        const utcString = dateString.includes('Z') ? dateString : dateString + 'Z';
        return new Date(utcString);
    };

    const fetchLeadsAndOpportunities = async () => {
        try {
            setIsRefreshing(true);
            const response = await fetch('https://api.casedb.co/leads.php?intake=true', { credentials: 'include' });
            const data = await response.json();
            if (data.success) {
                setLeads(data.leads || []);
                setOpportunities(data.opportunities || []);
                setLeadCounts({
                    today: data.leadCounts?.today || 0,
                    this_month: data.leadCounts?.this_month || 0,
                    last_month: data.leadCounts?.last_month || 0,
                    wanted_today: data.leadCounts?.wanted_today || 0,
                    wanted_this_month: data.leadCounts?.wanted_this_month || 0,
                    wanted_last_month: data.leadCounts?.wanted_last_month || 0,
                    signups_today: data.leadCounts?.status13_today || 0,
                    signups_this_month: data.leadCounts?.status13_this_month || 0,
                    signups_last_month: data.leadCounts?.status13_last_month || 0
                });
                setMessages(Array.isArray(data.recentExternalMessages) ? data.recentExternalMessages : []);
                setRecentlyUpdated(data.recentlyUpdated || []);
                setIsRefreshing(false);
                setShowRefreshCheck(true);
                setTimeout(() => setShowRefreshCheck(false), 300);
            } else {
                console.error('Failed to fetch data:', data.error);
                setIsRefreshing(false);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            setIsRefreshing(false);
        }
    };

    const fetchContactData = async (contactId) => {
        try {
            const response = await fetch(`https://api.casedb.co/contacts.php?id=${contactId}`, { credentials: 'include' });
            const data = await response.json();
            if (data.success) {
                setContactData(data.contacts);
                setEditContact(true);
            } else {
                setError({ message: 'Failed to fetch contact data', type: 'error' });
            }
        } catch (error) {
            setError({ message: 'Error fetching contact data', type: 'error' });
        }
    };

    const handleOpportunityClick = (opportunity) => {
        console.log('Opportunity clicked:', opportunity);
        fetchContactData(opportunity.id);
    };

    useEffect(() => {
        fetchLeadsAndOpportunities();

        document.title = "Intake Dashboard | CaseDB";
    }, []);

    return (
        <div className={`page-container ${navVariant === 'full' ? 'ab' : navVariant === 'compact' ? 'compact' : ''}`}>
            <div id="page-header" style={{ justifyContent: 'space-between' }}>
                <h2>Intake Overview</h2>
                <div className='reload small-text' onClick={fetchLeadsAndOpportunities}>
                    {showRefreshCheck ? (
                        <Check size={14} />
                    ) : (
                        <RefreshCw size={14} className={isRefreshing ? 'spinner small' : undefined} />
                    )}
                    Refresh
                </div>
            </div>
            <div className='intake'>
                <DataBox label='Leads' route={'leads'} values={[{ label: 'Today', data: leadCounts.today, route: 'leads?time=today' }, { label: 'This Month', data: leadCounts.this_month, route: 'leads?time=this_month' }, { label: 'Last Month', data: leadCounts.last_month, route: 'leads?time=last_month' }]} />
                <DataBox label='Wanted Leads' route={'leads?status=wanted'} values={[{ label: 'Today', data: leadCounts.wanted_today, route: 'leads?status=wanted&time=today' }, { label: 'This Month', data: leadCounts.wanted_this_month, route: 'leads?status=wanted&time=this_month' }, { label: 'Last Month', data: leadCounts.wanted_last_month, route: 'leads?status=wanted&time=last_month' }]} />
                <DataBox label='Signups' route={'leads?status=13'} values={[{ label: 'Today', data: leadCounts.signups_today, route: 'leads?status=13&time=today' }, { label: 'This Month', data: leadCounts.signups_this_month, route: 'leads?status=13&time=this_month' }, { label: 'Last Month', data: leadCounts.signups_last_month, route: 'leads?status=13&time=last_month' }]} />
                {/*<div className='opportunities intake-group'>
                    <Link to="/contacts?opportunities=true"><h4>Opportunities</h4></Link>
                    <table className='assigned-table subtext'>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Wait Time</th>
                                <th>Last Updated</th>
                            </tr>
                        </thead>
                        <tbody>
                            {opportunities.length > 0 ? opportunities.map((opp, index) => (
                                <tr key={index} className='lead-item' onClick={() => handleOpportunityClick(opp)}>
                                    <td>{opp.full_name}</td>
                                    <td>
                                        {formatDistanceToNow(
                                            parseUTCDate(opp.created_at),
                                            { addSuffix: false }
                                        )}
                                    </td>
                                    <td>
                                        {formatDistanceToNow(
                                            parseUTCDate(opp.updated_at),
                                            { addSuffix: true }
                                        )}
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan={4}>No opportunities available.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>*/}
                <div className='assigned-leads intake-group'>
                    <Link to="/leads?status=1"><h4>Assigned</h4></Link>
                    <table className='assigned-table subtext'>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Assigned</th>
                                <th>Wait Time</th>
                                <th>Case Type</th>
                                <th>Source</th>
                                <th>Rating</th>
                            </tr>
                        </thead>
                        <tbody>
                            {leads?.filter(lead => Number(lead.status) === 1)?.length > 0 ? (
                            leads?.filter(lead => Number(lead.status) === 1)?.map((lead, index) => (
                                <tr key={index} className='lead-item' onClick={() => navigate(`/intake/${lead.id}`)}>
                                    <td>{lead?.contact_name}</td>
                                    <td>{lead?.assigned_to}</td>
                                    <td className='wait-time'>
                                        <Clock size={14}/>{lead?.created_at
                                            ? formatDistanceToNow(parseUTCDate(lead.created_at), { addSuffix: true })
                                            : 'N/A'}
                                    </td>
                                    <td>{lead?.case_type_name}</td>
                                    <td>{lead?.marketing_source_name}</td>
                                    <td title={`${lead.case_likelihood} - ${caseLikelihood[lead.case_likelihood - 1]}`}>
                                        {Array.from({ length: lead.case_likelihood }, (_, index) => (
                                            <StarSharp key={index} className="star-icon" />
                                        ))}
                                        {Array.from({ length: 5 - lead.case_likelihood }, (_, index) => (
                                            <StarOutlineSharp key={index} className="star-icon empty" />
                                        ))}
                                    </td>
                                </tr>
                            ))) : (
                                <tr>
                                    <td colSpan={6}>No leads in Assigned status</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                <div className='messages intake-group'>
                    <h4>Messages</h4>
                    <table className='assigned-table subtext'>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Received</th>
                            </tr>
                        </thead>
                        <tbody>
                            {messages?.length > 0 ? messages?.map((msg) => (
                                <tr key={msg.id} className='lead-item' onClick={() => navigate(`/intake/${msg.lead_id}`)}>
                                    <td>{msg.name}</td>
                                    <td>
                                        <time dateTime={msg.received_at || undefined}>
                                            {formatIntakeMessageReceivedAt(msg.received_at)}
                                        </time>
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan={2}>No recent external messages.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                <div className='chase-leads intake-group'>
                    <Link to="/leads?status=5"><h4>Chase</h4></Link>
                    <table className='chase-table subtext'>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Assigned</th>
                                <th>Wait Time</th>
                                <th>Case Type</th>
                                <th>Source</th>
                                <th>Rating</th>
                            </tr>
                        </thead>
                        <tbody>
                            {leads?.filter(lead => Number(lead.status) === 5)?.length > 0 ? (
                                leads.filter(lead => Number(lead.status) === 5).map((lead, index) => (
                                    <tr key={index} className='lead-item' onClick={() => navigate(`/intake/${lead.id}`)}>
                                        <td>{lead?.contact_name}</td>
                                        <td>{lead?.assigned_to}</td>
                                        <td className='wait-time'>
                                            <Clock size={14}/>{lead?.created_at
                                                ? formatDistanceToNow(parseUTCDate(lead.created_at), { addSuffix: true })
                                                : 'N/A'}
                                        </td>
                                        <td>{lead?.case_type_name}</td>
                                        <td>{lead?.marketing_source_name}</td>
                                        <td title={`${lead.case_likelihood} - ${caseLikelihood[lead.case_likelihood - 1]}`}>
                                            {Array.from({ length: lead.case_likelihood }, (_, index) => (
                                                <StarSharp key={index} className="star-icon" />
                                            ))}
                                            {Array.from({ length: 5 - lead.case_likelihood }, (_, index) => (
                                                <StarOutlineSharp key={index} className="star-icon empty" />
                                            ))}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={6}>No leads in Chase status</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                <div className='pending-leads intake-group'>
                    <Link to="/leads?status=9"><h4>Pending Signup</h4></Link>
                    <table className='pending-table subtext'>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Assigned</th>
                                <th>Wait Time</th>
                                <th>Case Type</th>
                                <th>Source</th>
                                <th>Rating</th>
                            </tr>
                        </thead>
                        <tbody>
                            {leads?.filter(lead => Number(lead.status) === 9)?.length > 0 ? (
                                leads.filter(lead => Number(lead.status) === 9).map((lead, index) => (
                                    <tr key={index} className='lead-item' onClick={() => navigate(`/intake/${lead.id}`)}>
                                        <td>{lead?.contact_name}</td>
                                        <td>{lead?.assigned_to}</td>
                                        <td className='wait-time'>
                                            <Clock size={14}/>{lead?.created_at
                                                ? formatDistanceToNow(parseUTCDate(lead.created_at), { addSuffix: true })
                                                : 'N/A'}
                                        </td>
                                        <td>{lead?.case_type_name}</td>
                                        <td>{lead?.marketing_source_name}</td>
                                        <td title={`${lead.case_likelihood} - ${caseLikelihood[lead.case_likelihood - 1]}`}>
                                            {Array.from({ length: lead.case_likelihood }, (_, index) => (
                                                <StarSharp key={index} className="star-icon" />
                                            ))}
                                            {Array.from({ length: 5 - lead.case_likelihood }, (_, index) => (
                                                <StarOutlineSharp key={index} className="star-icon empty" />
                                            ))}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={6}>No leads in Pending Signup status</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                <div className='messages intake-group'>
                    <h4>Recently Updated Leads</h4>
                    <table className='assigned-table subtext'>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Updated</th>
                            </tr>
                        </thead>
                        <tbody>
                            {recentlyUpdated?.length > 0 ? recentlyUpdated?.map((update, index) => (
                                <tr key={index} className='lead-item' onClick={() => navigate(`/intake/${update.lead_id}`)}>
                                    <td>{update.contact_name}</td>
                                    <td>
                                        {formatDistanceToNow(
                                            new Date(update.time + 'Z'), // Add 'Z' to ensure UTC parsing, browser will convert to local
                                            { addSuffix: true }
                                        )}
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan={4}>No messages available.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                <div className='hold-leads intake-group'>
                    <Link to="/leads?status=6"><h4>Hold</h4></Link>
                    <table className='hold-table subtext'>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Assigned</th>
                                <th>Wait Time</th>
                                <th>Case Type</th>
                                <th>Source</th>
                                <th>Rating</th>
                            </tr>
                        </thead>
                        <tbody>
                            {leads?.filter(lead => Number(lead.status) === 6)?.length > 0 ? (
                                leads.filter(lead => Number(lead.status) === 6).map((lead, index) => (
                                    <tr key={index} className='lead-item' onClick={() => navigate(`/intake/${lead.id}`)}>
                                        <td>{lead?.contact_name}</td>
                                        <td>{lead?.assigned_to}</td>
                                        <td className='wait-time'>
                                            <Clock size={14}/>{lead?.created_at
                                                ? formatDistanceToNow(parseUTCDate(lead.created_at), { addSuffix: true })
                                                : 'N/A'}
                                        </td>
                                        <td>{lead?.case_type_name}</td>
                                        <td>{lead?.marketing_source_name}</td>
                                        <td title={`${lead.case_likelihood} - ${caseLikelihood[lead.case_likelihood - 1]}`}>
                                            {Array.from({ length: lead.case_likelihood }, (_, index) => (
                                                <StarSharp key={index} className="star-icon" />
                                            ))}
                                            {Array.from({ length: 5 - lead.case_likelihood }, (_, index) => (
                                                <StarOutlineSharp key={index} className="star-icon empty" />
                                            ))}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={6}>No leads in Hold status</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            
            {console.log('Render check - editContact:', editContact, 'contactData:', contactData)}
            {editContact && contactData && (
                <EditDetail
                    setEditContact={setEditContact}
                    contactData={contactData}
                    fetchContacts={fetchLeadsAndOpportunities}
                    setError={setError}
                />
            )}
        </div>
    );
};
